import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../app/hooks';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { RootState } from '../app/store';
import { updateUserProfile } from '../app/slices/authSlice';
import { addToast } from '../app/slices/uiSlice';
import { updateUserDistanceUnit } from '../lib/profile';
import { DistanceUnit } from '../types/models';
import { getProvinceConfig } from '../lib/taxEngine';
import { getAvailableProvinces } from '../lib/flags';
import provincesConfig from '../config/provinces.json';
import {
  CheckCircleIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { Navigate, useNavigate } from 'react-router-dom';

const Onboarding: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const { user } = useSelector((state: RootState) => state.auth);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProvince, setSelectedProvince] = useState(user?.province || ''); // NO DEFAULT
  const [selectedUnit] = useState<DistanceUnit>((user?.distanceUnit as DistanceUnit) || 'km');
  const [isLoading, setIsLoading] = useState(false);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const availableProvinceCodes = getAvailableProvinces();
  const supportedProvinces = availableProvinceCodes.map(code => ({
    code,
    name: (provincesConfig as any)[code]?.name || code,
  }));

  const handleFinish = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      await dispatch(updateUserProfile({ province: selectedProvince })).unwrap();
      await updateUserDistanceUnit(user.uid, selectedUnit);
      dispatch(
        addToast({
          message: 'Welcome to AutoTaxPilot! Your profile has been set up.',
          type: 'success',
        })
      );

      // Navigate to dashboard after successful setup
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 500); // Small delay to show success toast
    } catch (error: any) {
      console.error('[Onboarding] Failed to save profile:', error);
      dispatch(
        addToast({
          message: `Failed to save profile: ${error.message || 'Unknown error'}`,
          type: 'error',
        })
      );
      setIsLoading(false);
    }
  };

  const selectedProvinceConfig = getProvinceConfig(selectedProvince || 'ON'); // Fallback for display

  return (
    <div className="space-y-6">
      {/* Beautiful Centered Header */}
      <div className="text-center max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl shadow-lg mb-4">
            <span className="text-white font-bold text-2xl">A</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent mb-2">
            Welcome to AutoTaxPilot! 🚗
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-gray-600"
          >
            Let's set your province and distance unit. You can change these later in Profile.
          </motion.p>
        </motion.div>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="card">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              {[1, 2, 3].map(step => (
                <div
                  key={step}
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${step <= currentStep ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-500'}`}
                >
                  {step < currentStep ? <CheckCircleIcon className="h-5 w-5" /> : step}
                </div>
              ))}
            </div>
            <div className="mt-2 h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 bg-primary-600 rounded-full transition-all"
                style={{
                  width: `${(currentStep / 3) * 100}%`,
                  transitionDuration: shouldReduceMotion ? '0ms' : '300ms',
                }}
              />
            </div>
          </div>

          <AnimatePresence mode="wait">
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="text-center"
              >
                <MapPinIcon className="mx-auto h-12 w-12 text-primary-600 mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Welcome to AutoTaxPilot! 🚗
                </h2>
                <p className="text-gray-600 mb-4">
                  Which province do you primarily drive Uber/Lyft in?
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                  <p className="text-sm text-blue-800">
                    Sets your GST/HST rules. You can change it later in Profile. (Default: Ontario{' '}
                    {(getProvinceConfig('ON').rate * 100).toFixed(1)}% HST)
                  </p>
                </div>
                <div className="space-y-3 mb-6">
                  {supportedProvinces.map(province => {
                    const config = getProvinceConfig(province.code);
                    return (
                      <button
                        key={province.code}
                        onClick={() => setSelectedProvince(province.code)}
                        className={`w-full p-4 rounded-lg border-2 text-left transition-all ${selectedProvince === province.code ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="font-medium text-gray-900">{province.name}</h3>
                            <p className="text-sm text-gray-500">
                              {config.scheme} {(config.rate * 100).toFixed(1)}%
                            </p>
                          </div>
                          {selectedProvince === province.code && (
                            <CheckCircleIcon className="h-5 w-5 text-primary-600" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="text-center"
              >
                <BuildingOfficeIcon className="mx-auto h-12 w-12 text-primary-600 mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Business Structure</h2>
                <p className="text-gray-600 mb-6">
                  AutoTaxPilot is designed for sole proprietorship drivers.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">
                    ✅ Perfect for Sole Proprietors
                  </h3>
                  <ul className="text-sm text-blue-800 space-y-1 text-left">
                    <li>• Individual Uber/Lyft drivers</li>
                    <li>• Personal vehicle for rideshare</li>
                    <li>• Report income on personal tax return</li>
                    <li>• Rideshare GST/HST registration mandatory before first trip</li>
                  </ul>
                </div>
              </motion.div>
            )}

            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="text-center"
              >
                <DocumentTextIcon className="mx-auto h-12 w-12 text-primary-600 mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">You're All Set! 🎉</h2>
                <p className="text-gray-600 mb-6">
                  Your AutoTaxPilot is configured for {selectedProvinceConfig.name}.
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <h3 className="text-sm font-medium text-green-900 mb-2">Tax Configuration</h3>
                  <div className="text-sm text-green-800 space-y-1">
                    <p>
                      <strong>Province:</strong> {selectedProvinceConfig.name}
                    </p>
                    <p>
                      <strong>Tax Scheme:</strong> {selectedProvinceConfig.scheme}
                    </p>
                    <p>
                      <strong>Rate:</strong> {(selectedProvinceConfig.rate * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-8 flex justify-between">
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back
              </button>
            )}
            <div className="ml-auto">
              {currentStep < 3 ? (
                <button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={currentStep === 1 && !selectedProvince}
                  className="px-6 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleFinish}
                  disabled={isLoading}
                  className="px-6 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {isLoading ? 'Setting up...' : 'Get Started'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
